import React from 'react';
import { FileSearch, UsersRound, Share2, LineChart, Handshake } from 'lucide-react';

const steps = [
  {
    num: '01',
    icon: FileSearch,
    title: 'Content Evaluation',
    subtitle: 'Optimize & Refine',
    description: 'We assess your existing content using advanced tools and refine it to align with audience preferences and syndication standards.',
  },
  {
    num: '02',
    icon: UsersRound,
    title: 'Audience Identification',
    subtitle: 'Research & Target',
    description: 'We collaborate closely to identify your target audience preferences, pain points, and the ideal platforms for distribution.',
  },
  {
    num: '03',
    icon: Share2,
    title: 'Platform Syndication',
    subtitle: 'Distribute & Engage',
    description: 'Leveraging our network of 1,500+ vendors, we strategically distribute your content across high-authority platforms.',
  },
  {
    num: '04',
    icon: LineChart,
    title: 'Performance Tracking',
    subtitle: 'Measure & Report',
    description: 'Comprehensive analytics track reach, engagement, and lead conversion metrics to evaluate campaign impact in real-time.',
  },
  {
    num: '05',
    icon: Handshake,
    title: 'Lead Nurturing',
    subtitle: 'Convert & Grow',
    description: 'Our data-driven approach captures and qualifies leads, enabling effective nurturing of prospects into valuable customers.',
  },
];

export default function ProcessSection() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-20 md:py-32 bg-white"
      aria-label="Our Content Syndication Process"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block text-sm font-semibold tracking-wide uppercase text-[#4AC5DB] mb-4">
            Our Process
          </span>
          <h2
            data-testid="process-heading"
            className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight"
          >
            How Our B2B Content Syndication Works
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            A proven 5-step methodology for maximizing your content's impact
          </p>
        </div>

        {/* Desktop: Horizontal */}
        <div className="hidden md:grid grid-cols-5 gap-4 relative">
          {/* Connector line */}
          <div className="absolute top-[52px] left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-[#4AC5DB]/20 via-[#4AC5DB]/40 to-[#4AC5DB]/20 z-0" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                data-testid={`process-step-${i + 1}`}
                className="relative z-10 flex flex-col items-center text-center group"
              >
                <div className="w-[104px] h-[104px] rounded-2xl bg-white border-2 border-slate-100 group-hover:border-[#4AC5DB]/40 flex flex-col items-center justify-center transition-all duration-300 shadow-sm group-hover:shadow-md">
                  <Icon className="w-7 h-7 text-[#4AC5DB] mb-1" strokeWidth={1.5} />
                  <span className="font-['Outfit'] text-xs font-bold text-slate-400">{step.num}</span>
                </div>
                <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mt-5 mb-1">
                  {step.title}
                </h3>
                <p className="text-xs font-medium text-[#4AC5DB] mb-2">{step.subtitle}</p>
                <p className="text-sm text-slate-500 leading-relaxed px-2">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Mobile: Vertical timeline */}
        <div className="md:hidden space-y-0">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                data-testid={`process-step-mobile-${i + 1}`}
                className="flex gap-4 relative"
              >
                {/* Timeline line */}
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-white border-2 border-[#4AC5DB]/30 flex items-center justify-center shrink-0 z-10">
                    <Icon className="w-5 h-5 text-[#4AC5DB]" strokeWidth={1.5} />
                  </div>
                  {i < steps.length - 1 && (
                    <div className="w-[2px] flex-1 bg-gradient-to-b from-[#4AC5DB]/30 to-slate-100 my-1" />
                  )}
                </div>
                <div className="pb-8">
                  <span className="text-xs font-bold text-[#4AC5DB]">{step.num}</span>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mt-0.5 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
